/**
 * ENCONTRE O ERRO — quatro itens em que o aluno lê a resolução de um estudante fictício
 * e aponta o PRIMEIRO passo errado.
 *
 * O ERRO É SEMPRE UM ERRO NOMEADO: o mesmo catálogo que ancora os distratores da
 * múltipla escolha. Aqui ele não vira alternativa, vira um passo de resolução — e o
 * sorteio escolhe QUAL erro a resolução comete, o que muda o passo em que ele aparece.
 * A posição da chave varia com a variante, não com um embaralhamento.
 *
 * OS PASSOS DEPOIS DO ERRO SÃO COERENTES: herdam o valor errado e fazem certo o resto.
 * Por isso só um passo é "o primeiro errado", e apontar um passo posterior tem
 * diagnóstico próprio — o aluno viu que o resultado não bate, mas não achou onde o erro
 * nasceu.
 *
 * O TEXTO DOS PASSOS É NEUTRO: "Passo o 12 para o outro lado: 3x = 27 + 12" diz o que
 * o estudante fez, nunca que ele errou. O certo e o errado se escrevem com as mesmas
 * palavras; só a conta muda.
 */

import { formatBR, gcd } from '../../core/num.ts';
import type { Rng } from '../../core/rng.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import type { ErrorCode } from '../../schema/errors.ts';
import {
  ANG_DIAGONAIS_ERRADAS,
  ANG_SEM_SUBTRAIR,
  ANG_TRIANGULO_360,
  ARIT_CONTA,
  EQ_DISTRIBUTIVA,
  EQ_DIVISAO,
  EQ_SINAL,
  PCT_SINAL_TROCADO,
  PCT_SOBRE_INICIAL,
  PCT_SOMA_TAXAS,
  PCT_TAXA_COMO_FATOR,
  VEL_MEDIA_ARITMETICA,
} from '../../schema/errors.ts';
import type { CognitiveAxis } from '../../schema/taxonomy.ts';
import type { ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

const NOMES = ['Ana', 'Bruno', 'Carla', 'Davi', 'Elisa', 'Felipe', 'Gabriela', 'Heitor'] as const;

/** Número com o menos tipográfico e, se preciso, até duas casas decimais. */
function num(x: number): string {
  const r = Math.round(x * 100) / 100;
  const casas = Number.isInteger(r) ? 0 : Number.isInteger(r * 10) ? 1 : 2;
  const s = formatBR(Math.abs(r), casas);
  return r < 0 ? `−${s}` : s;
}

/** Fração irredutível, ou inteiro quando a divisão é exata: "23/3", "−5". */
function frac(p: number, q: number): string {
  if (q < 0) {
    p = -p;
    q = -q;
  }
  const g = gcd(Math.abs(p), q) || 1;
  const a = p / g;
  const b = q / g;
  return b === 1 ? num(a) : `${a < 0 ? '−' : ''}${Math.abs(a)}/${b}`;
}

/** Com espaço inseparável: "R$" e o valor não se separam na quebra de linha. */
const reais = (x: number): string => `R$\u00A0${formatBR(x, 2)}`;

/** A resolução como o estudante a escreveu, e o que o item sabe sobre ela. */
interface Resolucao {
  readonly passos: readonly RichText[];
  readonly passoErrado: number;
  readonly erro: ErrorCode;
  readonly explicacao: RichText;
  /** A resolução certa, para o comentário. */
  readonly certa: RichText;
}

interface Modelo<P extends { readonly nome: number }> {
  readonly id: string;
  /** Versão do gerador; sobe quando a questão muda (entra na semente). Padrão: 1. */
  readonly versao?: number;
  readonly codigo: string;
  readonly skills: readonly string[];
  readonly axis: CognitiveAxis;
  readonly titulo: string;
  readonly fontes: readonly DifficultySourceCode[];
  readonly params: (rng: Rng) => P;
  /** O problema que o estudante resolveu — o texto-base. */
  readonly problema: (p: P, nome: string) => RichText;
  readonly resolucao: (p: P) => Resolucao;
  /** Descarta variantes em que o erro, por coincidência, chega à resposta certa. */
  readonly valida?: (p: P) => string | null;
}

function criar<P extends { readonly nome: number }>(m: Modelo<P>): ItemTemplate<P, null> {
  return {
    id: templateId(m.id),
    version: m.versao ?? 1,
    taskModel: taskModelCode(m.codigo),
    skills: m.skills.map(skillCode),
    axis: m.axis,
    level: 'N0',
    title: m.titulo,
    // A resposta é um passo entre quatro: há acerto ao acaso, mas o item não tem
    // alternativas no sentido do catálogo — 2PL até haver dados para dizer mais.
    responseModel: '2PL',
    difficultySources: m.fontes,
    params: m.params,
    solve: () => null,
    buggy: [],
    variantDifficulty(p) {
      const fontes: DifficultySourceCode[] = [];
      // Erro no último passo: é preciso refazer todas as contas antes de achá-lo.
      if (m.resolucao(p).passoErrado === m.resolucao(p).passos.length - 1)
        fontes.push('steps.3plus');
      return fontes;
    },
    build(p) {
      const nome = NOMES[p.nome % NOMES.length] as string;
      const r = m.resolucao(p);
      return {
        stem: m.problema(p, nome),
        comando: [
          txt('A resolução tem um erro. '),
          strong('Em qual passo ele aparece pela primeira vez?'),
          txt(' Clique nesse passo.'),
        ],
        stimuli: [],
        interaction: {
          kind: 'error-step',
          passos: r.passos,
          passoErrado: r.passoErrado,
          erro: r.erro,
          explicacao: r.explicacao,
        },
        rationale: [strong('Resolução correta. '), ...r.certa],
        declared: r.passoErrado === r.passos.length - 1 ? 'dificil' : 'media',
      };
    },
    validate(_b, p) {
      const r = m.resolucao(p);
      if (r.passoErrado < 0 || r.passoErrado >= r.passos.length)
        return 'passo errado fora da resolução';
      return m.valida?.(p) ?? null;
    },
  };
}

// ---------------------------------------------------------------------------
// 1. Equação do 1º grau com parênteses
// ---------------------------------------------------------------------------

interface PEquacao {
  readonly nome: number;
  readonly a: number;
  readonly b: number;
  readonly x: number;
  readonly c: number;
  /** Onde a resolução erra: 0 distributiva, 1 sinal, 2 conta, 3 divisão. */
  readonly tipo: 0 | 1 | 2 | 3;
}

export const erroEquacao = criar<PEquacao>({
  id: 'k-erro-equacao',
  codigo: 'H1.6.K01',
  skills: ['H1.6', 'H1.10'],
  axis: 'III',
  titulo: 'Encontre o erro: equação do 1º grau',
  fontes: ['steps.3plus'],
  params(rng) {
    const a = rng.int(2, 6);
    const b = rng.int(2, 9);
    const x = rng.pick([-4, -3, -2, 2, 3, 4, 5, 6, 7, 8, 9]);
    return {
      nome: rng.int(0, NOMES.length - 1),
      a,
      b,
      x,
      c: a * (x + b),
      tipo: rng.int(0, 3) as 0 | 1 | 2 | 3,
    };
  },
  problema: (p, nome) => [
    txt(`${nome} resolveu a equação `),
    strong(`${p.a}(x + ${p.b}) = ${num(p.c)}`),
    txt(' e escreveu cada passo da resolução.'),
  ],
  // Com a = 2 e x = 2, subtrair 2 de 4 dá o mesmo que dividir 4 por 2: o erro chegaria à
  // resposta certa, e o aluno não teria como vê-lo pelo resultado.
  valida: (p) =>
    p.tipo === 3 && p.a * p.x - p.a === p.x ? 'o erro coincide com a resposta certa' : null,
  resolucao(p) {
    const ab = p.a * p.b;
    const k = p.tipo === 0 ? p.b : ab; // a constante depois da distributiva
    const soma = p.tipo === 1;
    let v = soma ? p.c + k : p.c - k;
    if (p.tipo === 2) v += 10;
    const passos: RichText[] = [
      [txt('Aplico a propriedade distributiva: '), strong(`${p.a}x + ${num(k)} = ${num(p.c)}`)],
      [
        txt(`Passo o ${num(k)} para o outro lado da igualdade: `),
        strong(`${p.a}x = ${num(p.c)} ${soma ? '+' : '−'} ${num(k)}`),
      ],
      [txt('Faço a conta: '), strong(`${p.a}x = ${num(v)}`)],
      p.tipo === 3
        ? [
            txt(`Passo o ${p.a} para o outro lado da igualdade: `),
            strong(`x = ${num(v)} − ${p.a} = ${num(v - p.a)}`),
          ]
        : [
            txt(`Passo o ${p.a} para o outro lado da igualdade: `),
            strong(`x = ${num(v)} ÷ ${p.a} = ${frac(v, p.a)}`),
          ],
    ];
    const certa: RichText = [
      txt(
        `${p.a}(x + ${p.b}) = ${num(p.c)} → ${p.a}x + ${num(ab)} = ${num(p.c)} → ${p.a}x = ${num(p.c)} − ${num(ab)} → `,
      ),
      txt(`${p.a}x = ${num(p.c - ab)} → x = ${num(p.c - ab)} ÷ ${p.a} = ${num(p.x)}.`),
      br,
      txt(
        `Conferindo: ${p.a} × (${num(p.x)} + ${p.b}) = ${p.a} × ${num(p.x + p.b)} = ${num(p.c)}.`,
      ),
    ];
    const erros: readonly [ErrorCode, RichText][] = [
      [
        EQ_DISTRIBUTIVA,
        [
          txt(
            `O ${p.a} multiplica tudo o que está nos parênteses — o x e o ${p.b}. A distributiva dá ${p.a}x + ${num(ab)} = ${num(p.c)}, e não ${p.a}x + ${p.b} = ${num(p.c)}.`,
          ),
        ],
      ],
      [
        EQ_SINAL,
        [
          txt(
            `O ${num(ab)} está somando no lado esquerdo; para tirá-lo de lá, subtrai-se ${num(ab)} dos dois lados: ${p.a}x = ${num(p.c)} − ${num(ab)}.`,
          ),
        ],
      ],
      [
        ARIT_CONTA,
        [txt(`A conta está errada: ${num(p.c)} − ${num(ab)} = ${num(p.c - ab)}, e não ${num(v)}.`)],
      ],
      [
        EQ_DIVISAO,
        [
          txt(
            `O ${p.a} está multiplicando o x. Para isolar o x, dividem-se os dois lados por ${p.a}: x = ${num(v)} ÷ ${p.a} = ${frac(v, p.a)}. Subtrair ${p.a} não desfaz uma multiplicação.`,
          ),
        ],
      ],
    ];
    const [erro, explicacao] = erros[p.tipo] as [ErrorCode, RichText];
    return { passos, passoErrado: p.tipo, erro, explicacao, certa };
  },
});

// ---------------------------------------------------------------------------
// 2. Velocidade média em dois trechos
// ---------------------------------------------------------------------------

interface PVelocidade {
  readonly nome: number;
  readonly v1: number;
  readonly t1: number;
  readonly v2: number;
  readonly t2: number;
}

/**
 * O ERRO É SEMPRE A MÉDIA DAS VELOCIDADES (v2, pedido do autor em 2026-09-29).
 *
 * A v1 sorteava entre quatro erros — tempo invertido, velocidade trocada, conta errada,
 * média das velocidades —, e o de conta ("3 + 4 = 8 h") saltava aos olhos: achar o
 * passo errado virava conferir aritmética. O erro que importa neste conteúdo é o
 * conceitual, e é ele que fica: as contas dos três primeiros passos estão todas certas,
 * e o último ignora o tempo que acabou de ser calculado e faz a média das duas
 * velocidades — (80 + 50) ÷ 2 = 65 km/h, quando a média da viagem é 440 ÷ 7.
 */
export const erroVelocidade = criar<PVelocidade>({
  id: 'k-erro-velocidade',
  versao: 2,
  codigo: 'H3.25.K01',
  skills: ['H3.25', 'H1.6'],
  axis: 'III',
  titulo: 'Encontre o erro: velocidade média',
  fontes: ['steps.3plus', 'unit-conversion'],
  params(rng) {
    const [v1, v2] = rng.shuffle([40, 50, 60, 80, 90]).slice(0, 2) as [number, number];
    // Tempos diferentes: com tempos iguais, a média das velocidades ACERTARIA.
    const [t1, t2] = rng.shuffle([2, 3, 4]).slice(0, 2) as [number, number];
    return { nome: rng.int(0, NOMES.length - 1), v1, t1, v2, t2 };
  },
  problema: (p, nome) => [
    txt(
      `Um ônibus percorreu ${num(p.v1 * p.t1)} km a ${p.v1} km/h e, em seguida, ${num(p.v2 * p.t2)} km a ${p.v2} km/h. `,
    ),
    txt(`${nome} calculou a velocidade média do ônibus na viagem inteira.`),
  ],
  resolucao(p) {
    const d1 = p.v1 * p.t1;
    const d2 = p.v2 * p.t2;
    const D = d1 + d2;
    const T = p.t1 + p.t2;
    const aprox = (x: number): string =>
      Math.abs(x * 100 - Math.round(x * 100)) > 1e-6 ? `≈ ${num(x)}` : `= ${num(x)}`;
    const passos: RichText[] = [
      [txt('Tempo no primeiro trecho: '), strong(`${d1} ÷ ${p.v1} = ${p.t1} h`)],
      [txt('Tempo no segundo trecho: '), strong(`${d2} ÷ ${p.v2} = ${p.t2} h`)],
      [
        txt('Distância total: '),
        strong(`${d1} + ${d2} = ${D} km`),
        txt('; tempo total: '),
        strong(`${p.t1} + ${p.t2} = ${T} h`),
      ],
      [
        txt('Velocidade média: '),
        strong(`(${p.v1} + ${p.v2}) ÷ 2 = ${num((p.v1 + p.v2) / 2)} km/h`),
      ],
    ];
    const certa: RichText = [
      txt(
        `t₁ = ${d1} ÷ ${p.v1} = ${p.t1} h; t₂ = ${d2} ÷ ${p.v2} = ${p.t2} h; distância total ${D} km, tempo total ${T} h. `,
      ),
      txt(`Velocidade média = ${D} ÷ ${T} ${aprox(D / T)} km/h.`),
    ];
    const explicacao: RichText = [
      txt(
        `As contas dos três primeiros passos estão certas; o erro está no último. A velocidade média não é a média das velocidades: o ônibus passou ${p.t1} h a ${p.v1} km/h e ${p.t2} h a ${p.v2} km/h, e a velocidade em que ele passou mais tempo pesa mais. ` +
          `É a distância total dividida pelo tempo total, que o próprio passo 3 já tinha calculado: ${D} ÷ ${T} ${aprox(D / T)} km/h, e não ${num((p.v1 + p.v2) / 2)} km/h.`,
      ),
    ];
    return { passos, passoErrado: 3, erro: VEL_MEDIA_ARITMETICA, explicacao, certa };
  },
});

// ---------------------------------------------------------------------------
// 3. Soma dos ângulos internos
// ---------------------------------------------------------------------------

interface PAngulos {
  readonly nome: number;
  readonly n: number;
  /** 0 diagonais, 1 triângulos, 2 soma do triângulo, 3 conta. */
  readonly tipo: 0 | 1 | 2 | 3;
}

export const erroAngulos = criar<PAngulos>({
  id: 'k-erro-angulos',
  codigo: 'H2.13.K05',
  skills: ['H2.13', 'H1.6'],
  axis: 'III',
  titulo: 'Encontre o erro: soma dos ângulos internos',
  fontes: ['steps.3plus'],
  params: (rng) => ({
    nome: rng.int(0, NOMES.length - 1),
    n: rng.int(7, 14),
    tipo: rng.int(0, 3) as 0 | 1 | 2 | 3,
  }),
  problema: (p, nome) => [
    txt(
      `${nome} calculou a soma das medidas dos ângulos internos de um polígono convexo de ${p.n} lados.`,
    ),
  ],
  resolucao(p) {
    const dg = p.tipo === 0 ? p.n - 2 : p.n - 3;
    const T = p.tipo === 1 ? p.n : dg + 1;
    const tri = p.tipo === 2 ? 360 : 180;
    const S = p.tipo === 3 ? T * tri + 100 : T * tri;
    const g = (x: number): string => `${formatBR(x, 0)}°`;
    const passos: RichText[] = [
      [
        txt('De um mesmo vértice saem '),
        strong(`${p.n} − ${p.tipo === 0 ? 2 : 3} = ${dg} diagonais.`),
      ],
      p.tipo === 1
        ? [
            txt('Essas diagonais dividem o polígono em '),
            strong(`${T} triângulos, um para cada lado.`),
          ]
        : [txt('Essas diagonais dividem o polígono em '), strong(`${dg} + 1 = ${T} triângulos.`)],
      [txt('A soma dos ângulos internos de cada triângulo é '), strong(`${g(tri)}.`)],
      [txt('Soma dos ângulos internos do polígono: '), strong(`${T} × ${g(tri)} = ${g(S)}.`)],
    ];
    const certa: RichText = [
      txt(
        `De um vértice saem ${p.n} − 3 = ${p.n - 3} diagonais, que dividem o polígono em ${p.n - 2} triângulos. `,
      ),
      txt(`Soma = ${p.n - 2} × 180° = ${g((p.n - 2) * 180)}.`),
    ];
    const erros: readonly [ErrorCode, RichText][] = [
      [
        ANG_DIAGONAIS_ERRADAS,
        [
          txt(
            `De um vértice saem ${p.n} − 3 = ${p.n - 3} diagonais: o vértice não se liga a si mesmo nem aos dois vizinhos.`,
          ),
        ],
      ],
      [
        ANG_SEM_SUBTRAIR,
        [
          txt(
            `${dg} diagonais dividem o polígono em ${dg} + 1 = ${dg + 1} triângulos, e não em um por lado.`,
          ),
        ],
      ],
      [
        ANG_TRIANGULO_360,
        [txt('Os ângulos internos de um triângulo somam 180°; 360° é a soma de um quadrilátero.')],
      ],
      [
        ARIT_CONTA,
        [txt(`A multiplicação está errada: ${T} × 180° = ${g(T * 180)}, e não ${g(S)}.`)],
      ],
    ];
    const [erro, explicacao] = erros[p.tipo] as [ErrorCode, RichText];
    return { passos, passoErrado: p.tipo, erro, explicacao, certa };
  },
});

// ---------------------------------------------------------------------------
// 4. Aumento seguido de desconto
// ---------------------------------------------------------------------------

interface PReajuste {
  readonly nome: number;
  readonly P: number;
  readonly a: number;
  readonly d: number;
  /** 0 fator do aumento, 1 fator do desconto, 2 sobre o inicial, 3 soma das taxas. */
  readonly tipo: 0 | 1 | 2 | 3;
}

export const erroReajustes = criar<PReajuste>({
  id: 'k-erro-reajustes',
  codigo: 'H1.6.K02',
  skills: ['H1.6'],
  axis: 'III',
  titulo: 'Encontre o erro: aumento seguido de desconto',
  fontes: ['steps.3plus'],
  params(rng) {
    const [a, d] = rng.shuffle([10, 20, 25, 30, 40, 50]).slice(0, 2) as [number, number];
    return {
      nome: rng.int(0, NOMES.length - 1),
      P: rng.pick([200, 400, 500, 800, 1000, 1200, 1500, 2000]),
      a,
      d,
      tipo: rng.int(0, 3) as 0 | 1 | 2 | 3,
    };
  },
  problema: (p, nome) => [
    txt(
      `Uma loja aumentou em ${p.a}% o preço de um produto que custava ${reais(p.P)} e, no mês seguinte, deu um desconto de ${p.d}% sobre o novo preço. `,
    ),
    txt(`${nome} calculou o preço final e a variação em relação ao preço inicial.`),
  ],
  resolucao(p) {
    const f1 = p.tipo === 0 ? 1 - p.a / 100 : 1 + p.a / 100;
    const f2 = p.tipo === 1 ? p.d / 100 : 1 - p.d / 100;
    const F = p.tipo === 2 ? p.P + (p.a / 100) * p.P - (p.d / 100) * p.P : p.P * f1 * f2;
    const varF = ((F - p.P) / p.P) * 100;
    const pct = (x: number): string => `${num(x)}%`;
    const passos: RichText[] = [
      [
        txt(`Fator do aumento de ${p.a}%: `),
        strong(`1 ${p.tipo === 0 ? '−' : '+'} ${p.a}/100 = ${num(f1)}`),
      ],
      [
        txt(`Fator do desconto de ${p.d}%: `),
        strong(p.tipo === 1 ? `${p.d}/100 = ${num(f2)}` : `1 − ${p.d}/100 = ${num(f2)}`),
      ],
      p.tipo === 2
        ? [
            txt('Preço final: '),
            strong(
              `${reais(p.P)} + ${p.a}% de ${reais(p.P)} − ${p.d}% de ${reais(p.P)} = ${reais(F)}`,
            ),
          ]
        : [txt('Preço final: '), strong(`${reais(p.P)} × ${num(f1)} × ${num(f2)} = ${reais(F)}`)],
      p.tipo === 3
        ? [
            txt('Variação em relação ao preço inicial: '),
            strong(`${p.a}% − ${p.d}% = ${pct(p.a - p.d)}`),
          ]
        : [
            txt('Variação em relação ao preço inicial: '),
            strong(`(${reais(F)} − ${reais(p.P)}) ÷ ${reais(p.P)} = ${pct(varF)}`),
          ],
    ];
    const Fc = p.P * (1 + p.a / 100) * (1 - p.d / 100);
    const certa: RichText = [
      txt(
        `Fatores: 1 + ${p.a}/100 = ${num(1 + p.a / 100)} e 1 − ${p.d}/100 = ${num(1 - p.d / 100)}. `,
      ),
      txt(
        `Preço final: ${reais(p.P)} × ${num(1 + p.a / 100)} × ${num(1 - p.d / 100)} = ${reais(Fc)}. `,
      ),
      txt(
        `Variação: (${reais(Fc)} − ${reais(p.P)}) ÷ ${reais(p.P)} = ${pct(((Fc - p.P) / p.P) * 100)}.`,
      ),
    ];
    const erros: readonly [ErrorCode, RichText][] = [
      [
        PCT_SINAL_TROCADO,
        [
          txt(
            `Um aumento de ${p.a}% multiplica o preço por 1 + ${p.a}/100 = ${num(1 + p.a / 100)}; o fator 1 − ${p.a}/100 é o de um desconto.`,
          ),
        ],
      ],
      [
        PCT_TAXA_COMO_FATOR,
        [
          txt(
            `Multiplicar por ${p.d}/100 dá o VALOR do desconto. O preço depois do desconto é multiplicado por 1 − ${p.d}/100 = ${num(1 - p.d / 100)}.`,
          ),
        ],
      ],
      [
        PCT_SOBRE_INICIAL,
        [
          txt(
            `O desconto foi dado sobre o NOVO preço, ${reais(p.P * (1 + p.a / 100))}, e não sobre ${reais(p.P)}: calculado sobre o preço inicial, ele sai menor do que foi.`,
          ),
        ],
      ],
      [
        PCT_SOMA_TAXAS,
        [
          txt(
            `Porcentagens aplicadas uma depois da outra não se somam nem se subtraem: a variação sai do preço final, ${pct(((F - p.P) / p.P) * 100)}.`,
          ),
        ],
      ],
    ];
    const [erro, explicacao] = erros[p.tipo] as [ErrorCode, RichText];
    return { passos, passoErrado: p.tipo, erro, explicacao, certa };
  },
});
